import { FeedbackSnackbar, IdentityAvatar, InlineNotice, useFeedbackSnackbar } from '../../../design-system';
import { CameraAltOutlined, DeleteOutlined } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, ListItemIcon, MenuItem, Stack } from '@mui/material';
import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { AnchoredMenu, FormDialog, createFocusRingStyles, designTokens } from '../../../design-system';
import type { ProfilePhotoPort } from './port';

type ProfilePhotoControlProps = {
  port: ProfilePhotoPort;
  personId: string;
  name: string;
  photoUrl?: string;
  fixtureMode: boolean;
  presentation?: 'standalone' | 'profileHeader';
  readOnly?: boolean;
  onChanged?: (url?: string) => void;
};

const maxBytes = 8 << 20;

export function ProfilePhotoControl({ port, personId, name, photoUrl, fixtureMode, presentation = 'standalone', readOnly = false, onChanged }: ProfilePhotoControlProps) {
  const { notify, dismiss, feedbackProps } = useFeedbackSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);
  const menuId = useId();
  const [currentUrl, setCurrentUrl] = useState(photoUrl);
  const [selectedFile, setSelectedFile] = useState<File>();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const initial = Array.from(name || '나')[0] ?? '나';
  const profileHeader = presentation === 'profileHeader';
  const size = profileHeader ? { xs: 64, sm: 92 } : 92;

  useEffect(() => { setCurrentUrl(photoUrl); }, [personId, photoUrl]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const choose = () => {
    setMenuAnchor(null);
    if (!readOnly) inputRef.current?.click();
  };
  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    dismiss();
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setError('JPG 또는 PNG 사진을 선택해 주세요.');
      return;
    }
    if (file.size > maxBytes) {
      setError('프로필 사진은 8MB 이하로 선택해 주세요.');
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const nextPreview = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(nextPreview);
    setError(undefined);
    setOpen(true);
  };
  const close = () => {
    if (busy) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(undefined);
    setPreviewUrl(undefined);
    setOpen(false);
  };
  const save = () => {
    if (!selectedFile || !previewUrl) return;
    setBusy(true);
    setError(undefined);
    const fixtureUrl = fixtureMode ? URL.createObjectURL(selectedFile) : undefined;
    const request = fixtureMode
      ? Promise.resolve({ url: fixtureUrl as string })
      : port.uploadProfilePhoto(personId, selectedFile);
    void request.then((photo) => {
      const nextUrl = photo.url;
      if (fixtureMode && currentUrl?.startsWith('blob:')) URL.revokeObjectURL(currentUrl);
      setCurrentUrl(nextUrl);
      notify('프로필 사진을 저장했습니다.', { tone: 'success' });
      setSelectedFile(undefined);
      setPreviewUrl(undefined);
      setOpen(false);
      onChanged?.(nextUrl);
    }, (reason: unknown) => {
      if (fixtureUrl) URL.revokeObjectURL(fixtureUrl);
      setError(reason instanceof Error ? reason.message : '프로필 사진을 저장하지 못했습니다.');
    }).finally(() => setBusy(false));
  };
  const remove = () => {
    if (!currentUrl || busy) return;
    setBusy(true);
    setError(undefined);
    const request = fixtureMode ? Promise.resolve() : port.deleteProfilePhoto(personId);
    void request.then(() => {
      if (fixtureMode && currentUrl.startsWith('blob:')) URL.revokeObjectURL(currentUrl);
      setCurrentUrl(undefined);
      notify('프로필 사진을 삭제했습니다.', { tone: 'success' });
      setDeleteOpen(false);
      onChanged?.(undefined);
    }, (reason: unknown) => {
      setError(reason instanceof Error ? reason.message : '프로필 사진을 삭제하지 못했습니다.');
    }).finally(() => setBusy(false));
  };

  return <>
    <Stack spacing={profileHeader ? 0.5 : 1} sx={{ alignItems: profileHeader ? 'flex-start' : 'center' }}>
      {readOnly ? <IdentityAvatar
        src={currentUrl}
        alt={`${name} 프로필 사진`}
        sx={{ width: size, height: size, fontSize: 28, fontWeight: 760, border: `3px solid ${designTokens.color.canvas}`, boxShadow: `0 0 0 1px ${designTokens.color.strongDivider}` }}
      >{initial}</IdentityAvatar> : <IconButton
        aria-label={`${name} 프로필 사진 메뉴`}
        aria-haspopup="menu"
        aria-expanded={Boolean(menuAnchor)}
        aria-controls={menuAnchor ? menuId : undefined}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        disabled={busy}
        sx={{ position: 'relative', width: size, height: size, p: 0, overflow: 'visible', '&:focus-visible': createFocusRingStyles() }}
      >
        <IdentityAvatar
          src={currentUrl}
          alt=""
          sx={{ width: size, height: size, fontSize: 28, fontWeight: 760, border: `3px solid ${designTokens.color.canvas}`, boxShadow: `0 0 0 1px ${designTokens.color.strongDivider}` }}
        >{initial}</IdentityAvatar>
        <Box sx={{ position: 'absolute', right: -2, bottom: -2, width: 30, height: 30, display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.circle, bgcolor: designTokens.color.action, color: designTokens.color.inverseText, border: `2px solid ${designTokens.color.canvas}` }}>
          {busy ? <CircularProgress size={14} color="inherit" /> : <CameraAltOutlined sx={{ fontSize: 16 }} />}
        </Box>
      </IconButton>}
      {error && <InlineNotice severity="error" announcement="polite" density="compact">{error}</InlineNotice>}
    </Stack>
    <FeedbackSnackbar {...feedbackProps} />
    {!readOnly && <input ref={inputRef} type="file" accept="image/jpeg,image/png" hidden onChange={onFile} />}
    {!readOnly && <AnchoredMenu
      id={menuId}
      anchorEl={menuAnchor}
      open={Boolean(menuAnchor)}
      onClose={() => setMenuAnchor(null)}
    >
      <MenuItem onClick={choose} disabled={busy}>
        <ListItemIcon><CameraAltOutlined fontSize="small" /></ListItemIcon>
        {currentUrl ? '프로필 사진 변경' : '프로필 사진 등록'}
      </MenuItem>
      {currentUrl && <MenuItem
        onClick={() => { setMenuAnchor(null); setDeleteOpen(true); }}
        disabled={busy}
        sx={{ color: designTokens.color.danger }}
      >
        <ListItemIcon sx={{ color: 'inherit' }}><DeleteOutlined fontSize="small" /></ListItemIcon>
        프로필 사진 삭제
      </MenuItem>}
    </AnchoredMenu>}
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={save}
      title="프로필 사진 확인"
      help="저장하면 정사각형으로 맞추고 사진의 위치 정보 등 메타데이터를 제거합니다."
      cancelLabel="다시 선택"
      submitLabel="이 사진 사용"
      busy={busy}
      busyLabel="저장 중"
      submitDisabled={!selectedFile || !previewUrl}
      maxWidth="xs"
    >
        <Stack spacing={2} sx={{ alignItems: 'center', pt: 1 }}>
          <IdentityAvatar src={previewUrl} alt="선택한 프로필 사진 미리보기" sx={{ width: 176, height: 176, bgcolor: designTokens.color.subtleSurface, boxShadow: `0 0 0 1px ${designTokens.color.strongDivider}` }} />
          {error && <InlineNotice severity="error" announcement="polite">{error}</InlineNotice>}
        </Stack>
    </FormDialog>
    <FormDialog
      open={deleteOpen}
      onClose={() => setDeleteOpen(false)}
      onSubmit={remove}
      title="프로필 사진 삭제"
      cancelLabel="취소"
      submitLabel="사진 삭제"
      busy={busy}
      busyLabel="삭제 중"
      submitColor="error"
      maxWidth="xs"
    >
      <InlineNotice severity="warning">삭제한 사진은 복구할 수 없습니다. 새 사진은 언제든 다시 등록할 수 있습니다.</InlineNotice>
    </FormDialog>
  </>;
}
